import React, { useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { API_URL } from "../../../_services/apiUrl";
import { get, post } from "../../../_services/apiService";
import Layout from "../../Shared/Layout";
import { useNavigate, useParams } from "react-router-dom";
import RatingAndWishlist from "./RatingAndWishlist";
import { useWishlist } from "../../../context/wishlistContext";
import { formatCurrencyAmount } from "../../../utils/currency";

const Products = () => {
    const navigate = useNavigate();
    const { categoryId } = useParams();

    const { guestIdInt, ipAddress, addToWishlist, removeFromWishlist, wishlist } = useWishlist();

    const [products, setProducts] = useState([]);
    const [productCategoryData, setProductCategoryData] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedPriceRanges, setSelectedPriceRanges] = useState([]);
    const [selectedSort, setSelectedSort] = useState("default");
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const wishlistData = useMemo(() => {
        if (Array.isArray(wishlist)) return wishlist;
        if (Array.isArray(wishlist?.data)) return wishlist.data;
        if (Array.isArray(wishlist?.wishlist)) return wishlist.wishlist;
        return [];
    }, [wishlist]);

    const getWishlistProductId = (wishItem) => {
        return Number(
            wishItem?.product_id ??
            wishItem?.productId ??
            wishItem?.id ??
            0
        );
    };

    const isItemWishlisted = (productId) => {
        return wishlistData.some(
            (wishItem) => getWishlistProductId(wishItem) === Number(productId)
        );
    };

    useEffect(() => {
        if (!guestIdInt || ipAddress === "") return;

        const fetchProducts = async () => {
            try {
                setLoading(true);
                setError(false);

                let response;

                const params = {
                    guest_id: guestIdInt,
                    ip_address: ipAddress,
                    category_id:categoryId
                };

                if (categoryId) {
                    response = await post(`${API_URL.categoriesbyId}`, params);
                } else {
                    response = await post(API_URL.products, params);
                }

                const finalProducts = Array.isArray(response)
                    ? response
                    : response?.products ||
                    response?.product ||
                    response?.data?.products ||
                    response?.data?.product ||
                    response?.data ||
                    [];

                setProducts(Array.isArray(finalProducts) ? finalProducts : []);

                if (categoryId) {
                    setSelectedCategories([Number(categoryId)]);
                } else {
                    setSelectedCategories([]);
                }
            } catch (fetchError) {
                setError(true);
                setProducts([]);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [guestIdInt, categoryId]);

    useEffect(() => {
        if (Array.isArray(products) && products.length > 0) {
            const uniqueCategories = [];
            const usedIds = new Set();

            products.forEach((item) => {
                const currentCategoryId = Number(item?.category_id || 0);

                if (currentCategoryId && !usedIds.has(currentCategoryId)) {
                    usedIds.add(currentCategoryId);
                    uniqueCategories.push({
                        productCategoryId: currentCategoryId,
                        displayName: item?.category_name || "Category",
                    });
                }
            });

            setProductCategoryData(uniqueCategories);
        } else {
            setProductCategoryData([]);
        }
    }, [products]);

    const truncateProductName = (productName, maxWords = 3) => {
        if (!productName) return "";
        const words = productName.split(" ");
        if (words.length <= maxWords) return productName;
        return `${words.slice(0, maxWords).join(" ")}...`;
    };

    const onProductDetail = (item) => {
        if (!item) return;

        localStorage.removeItem("cartItemDetail");
        localStorage.removeItem("itemDetail");

        if (item?.id) {
            localStorage.setItem("productId", item.id);
        }

        const productSlug =
            item?.slug || item?.name?.replace(/\s+/g, "-") || "product";

        const url = `/product/${productSlug}`;

        navigate(url);
        localStorage.setItem("priviousUrl", url);
    };

    const toggleSidebar = () => {
        setIsSidebarVisible((prev) => !prev);
    };

    const handleSortChange = (e) => {
        setSelectedSort(e.target.value);
    };

    const handleCheckboxChange = (clickedCategoryId) => {
        const numericCategoryId = Number(clickedCategoryId);

        setSelectedCategories((prev) => {
            const alreadySelected = prev.includes(numericCategoryId);

            return alreadySelected
                ? prev.filter((id) => id !== numericCategoryId)
                : [...prev, numericCategoryId];
        });
    };

    const handlePriceCheckboxChange = (priceRange) => {
        setSelectedPriceRanges((prev) =>
            prev.includes(priceRange)
                ? prev.filter((range) => range !== priceRange)
                : [...prev, priceRange]
        );
    };

    const handleClearFilters = () => {
        setSelectedPriceRanges([]);
        setSelectedSort("default");

        if (categoryId) {
            setSelectedCategories([Number(categoryId)]);
        } else {
            setSelectedCategories([]);
        }
    };

    const filteredProducts = useMemo(() => {
        let filtered = Array.isArray(products) ? [...products] : [];

        filtered = filtered.filter((product) => {
            const productPrice = Number(
                product?.unit_price ??
                product?.price ??
                product?.purchase_price ??
                0
            );

            const productCategoryId = Number(product?.category_id || 0);

            const isInCategory =
                selectedCategories.length === 0 ||
                selectedCategories.includes(productCategoryId);

            const isInPriceRange =
                selectedPriceRanges.length === 0 ||
                selectedPriceRanges.some((range) => {
                    switch (range) {
                        case "0-399":
                            return productPrice >= 0 && productPrice <= 399;
                        case "400-799":
                            return productPrice >= 400 && productPrice <= 799;
                        case "800-1099":
                            return productPrice >= 800 && productPrice <= 1099;
                        case "1100-1999":
                            return productPrice >= 1100 && productPrice <= 1999;
                        case "2000+":
                            return productPrice >= 2000;
                        default:
                            return true;
                    }
                });

            return isInCategory && isInPriceRange;
        });

        if (selectedSort === "priceLowToHigh") {
            filtered.sort(
                (a, b) =>
                    Number(a?.unit_price ?? a?.price ?? 0) -
                    Number(b?.unit_price ?? b?.price ?? 0)
            );
        } else if (selectedSort === "priceHighToLow") {
            filtered.sort(
                (a, b) =>
                    Number(b?.unit_price ?? b?.price ?? 0) -
                    Number(a?.unit_price ?? a?.price ?? 0)
            );
        }

        return filtered;
    }, [products, selectedCategories, selectedPriceRanges, selectedSort]);

    const showEmptyState = !loading && (error || filteredProducts.length === 0);

    const onWishlistToggle = async (item) => {
        try {
            const token = localStorage.getItem("token");
            const isLoggedIn = localStorage.getItem("isLoggedIn");

            if (isLoggedIn !== "true" || !token) {
                toast.error("Please login first!");

                setTimeout(() => {
                    navigate("/login");
                }, 1000);

                return;
            }

            if (!item?.id) {
                toast.error("Invalid product");
                return;
            }

            const alreadyWishlisted = isItemWishlisted(item?.id);

            if (alreadyWishlisted) {
                await removeFromWishlist(item.id);
                toast.success("Removed from wishlist!");
            } else {
                await addToWishlist({
                    product_id: item?.id,
                });
                toast.success("Added to wishlist!");

                setTimeout(() => {
                    navigate("/wishlist");
                }, 1200);
            }
        } catch (err) {
            toast.error("Wishlist action failed");
        }
    };

    return (
        <Layout pageTitle="Shop" breadcrumbLabel="Shop">
            <Toaster position="top-right" />

            <section className="same_space">
                <div className="container">
                    {showEmptyState ? (
                        <div className="shop-empty-state">
                            <div className="shop-empty-state-graphic" aria-hidden="true">
                                <span className="shop-empty-state-mark shop-empty-state-mark-main">?</span>
                                <span className="shop-empty-state-mark shop-empty-state-mark-left">o</span>
                                <span className="shop-empty-state-mark shop-empty-state-mark-right">o</span>
                                <span className="shop-empty-state-mark shop-empty-state-mark-bottom">+</span>
                                <svg
                                    viewBox="0 0 180 180"
                                    className="shop-empty-state-image"
                                    role="img"
                                    aria-label="No products found"
                                >
                                    <path
                                        d="M62 50C62 32 74 18 90 18C106 18 118 32 118 50"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="6"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M48 52H132L144 150H36L48 52Z"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="6"
                                        strokeLinejoin="round"
                                    />
                                    <circle cx="72" cy="92" r="6" fill="currentColor" />
                                    <circle cx="108" cy="92" r="6" fill="currentColor" />
                                    <path
                                        d="M78 120C80 112 86 108 90 108C94 108 100 112 102 120"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </div>
                            <h3>No Products Avaiable</h3>
                        </div>
                    ) : (
                        <>
                            <div className="hedding_product row justify-content-end align-items-end py-sm-3 py-1">
                                <div className="icon_wepper_filter col-md-12 col-7 d-sm-flex align-items-center justify-content-sm-end">
                                    <h6 className="mb-1" style={{ paddingRight: "10px" }}>
                                        Sort by :
                                    </h6>
                                    <div>
                                        <select
                                            value={selectedSort}
                                            onChange={handleSortChange}
                                            className="sort_by_select"
                                        >
                                            <option value="default">All</option>
                                            <option value="priceLowToHigh">Low to High</option>
                                            <option value="priceHighToLow">High to Low</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="filter_toggle_mobile col-5">
                                    <button onClick={toggleSidebar}>
                                        Filter
                                        <span>
                                            <svg
                                                id="Layer_1"
                                                height="22"
                                                width="22"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g
                                                    width="100%"
                                                    height="100%"
                                                    transform="matrix(1,0,0,1,0,0)"
                                                >
                                                    <switch>
                                                        <g>
                                                            <path
                                                                d="m18 24h-12c-3.3 0-6-2.7-6-6v-12c0-3.3 2.7-6 6-6h12c3.3 0 6 2.7 6 6v12c0 3.3-2.7 6-6 6z"
                                                                fill="#ffffff"
                                                            />
                                                            <path
                                                                d="m12 15.5c-.2 0-.5-.1-.6-.2l-6-5c-.4-.4-.5-1-.1-1.4s1-.5 1.4-.1l5.4 4.5 5.4-4.3c.4-.3 1.1-.3 1.4.1.4.4.3 1-.1 1.4 0 0 0 0-.1 0l-6 4.8c-.3.2-.5.2-.7.2z"
                                                                fill="#000000"
                                                            />
                                                        </g>
                                                    </switch>
                                                </g>
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div className="row mt-sm-4 mt-3">
                                <div
                                    className={`col-md-3 col-12 filter_sidebar ${isSidebarVisible ? "show" : ""}`}
                                >
                                    <div className="widget sidebar-tags">
                                        <div className="widget-title open">
                                            <h3 className="sidebar-title text-uppercase">
                                                <span>Shop by Category</span>
                                            </h3>
                                        </div>

                                        <div className="widget-content d-block">
                                            {productCategoryData.length > 0 ? (
                                                <ul className="list-tags p-0 list-unstyled">
                                                    {productCategoryData.map((item, i) => (
                                                        <li className="item" key={i}>
                                                            <input
                                                                type="checkbox"
                                                                value={item.productCategoryId}
                                                                checked={selectedCategories.includes(
                                                                    item.productCategoryId
                                                                )}
                                                                onChange={() =>
                                                                    handleCheckboxChange(item.productCategoryId)
                                                                }
                                                            />
                                                            <label>{item?.displayName}</label>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p>No data available</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="widget sidebar-tags pt-sm-4 pt-2">
                                        <div className="widget-title open">
                                            <h3 className="sidebar-title text-uppercase">
                                                <span>Shop by Price</span>
                                            </h3>
                                        </div>

                                        <div className="widget-content d-block">
                                            <ul className="list-tags p-0 list-unstyled">
                                                {[
                                                    {
                                                        label: "0-399",
                                                        exists: products.some((p) => {
                                                            const price = Number(
                                                                p?.unit_price ?? p?.price ?? p?.purchase_price ?? 0
                                                            );
                                                            return price >= 0 && price <= 399;
                                                        }),
                                                    },
                                                    {
                                                        label: "400-799",
                                                        exists: products.some((p) => {
                                                            const price = Number(
                                                                p?.unit_price ?? p?.price ?? p?.purchase_price ?? 0
                                                            );
                                                            return price >= 400 && price <= 799;
                                                        }),
                                                    },
                                                    {
                                                        label: "800-1099",
                                                        exists: products.some((p) => {
                                                            const price = Number(
                                                                p?.unit_price ?? p?.price ?? p?.purchase_price ?? 0
                                                            );
                                                            return price >= 800 && price <= 1099;
                                                        }),
                                                    },
                                                    {
                                                        label: "1100-1999",
                                                        exists: products.some((p) => {
                                                            const price = Number(
                                                                p?.unit_price ?? p?.price ?? p?.purchase_price ?? 0
                                                            );
                                                            return price >= 1100 && price <= 1999;
                                                        }),
                                                    },
                                                    {
                                                        label: "2000+",
                                                        exists: products.some((p) => {
                                                            const price = Number(
                                                                p?.unit_price ?? p?.price ?? p?.purchase_price ?? 0
                                                            );
                                                            return price >= 2000;
                                                        }),
                                                    },
                                                ]
                                                    .filter((item) => item.exists)
                                                    .map((range, i) => (
                                                        <li className="item" key={i}>
                                                            <input
                                                                type="checkbox"
                                                                value={range.label}
                                                                checked={selectedPriceRanges.includes(range.label)}
                                                                onChange={() =>
                                                                    handlePriceCheckboxChange(range.label)
                                                                }
                                                            />

                                                            <label>
                                                                {range.label
                                                                    .replace("-", " to ")
                                                                    .replace("+", " Above")}
                                                            </label>
                                                        </li>
                                                    ))}
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="filter_buttons pt-2 d-flex align-items-center">
                                        <button
                                            onClick={handleClearFilters}
                                            className="filter_clear_button"
                                        >
                                            Clear
                                        </button>
                                        <button
                                            onClick={() => setIsSidebarVisible(false)}
                                            className="filter_apply__button"
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>

                                <div className="col-md-9 col-12">
                                    {loading ? (
                                        <p>Loading...</p>
                                    ) : (
                                        <div className="shop_flex_weppr row">
                                            {filteredProducts.map((item, index) => {
                                                const rating = Number(
                                                    item?.rating?.[0]?.average ||
                                                    item?.average_review ||
                                                    0
                                                );

                                                const imagePath =
                                                    item?.thumbnail_full_url?.path ||
                                                    item?.thumbnail_full_url ||
                                                    item?.images?.[0] ||
                                                    "./images/defaultImage.png";

                                                const productName = item?.name || "Product";
                                                const productPrice =
                                                    item?.unit_price ??
                                                    item?.price ??
                                                    item?.purchase_price ??
                                                    0;

                                                return (
                                                    <div
                                                        key={item?.id || index}
                                                        className="shop_inner col-lg-3 col-sm-4 col-6 mb-sm-4 mb-0 pb-sm-4 pb-3"
                                                    >
                                                        <div onClick={() => onProductDetail(item)}>
                                                            <img
                                                                className="curser-pointer"
                                                                src={imagePath}
                                                                alt={productName}
                                                                width="100%"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src =
                                                                        "./images/defaultImage.png";
                                                                }}
                                                            />

                                                            <h2
                                                                className="curser-pointer"
                                                                title={productName}
                                                            >
                                                                {truncateProductName(productName)}
                                                            </h2>

                                                            <h4 className="price text-white">
                                                                {formatCurrencyAmount(productPrice, item)}
                                                            </h4>
                                                        </div>

                                                        <RatingAndWishlist
                                                            item={item}
                                                            rating={rating}
                                                            onWishlistToggle={onWishlistToggle}
                                                            isWishlisted={isItemWishlisted(item?.id)}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </Layout>
    );
};

export default Products;
